import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, TextInput, InlineNotification } from '@carbon/react';
import { Password, Checkmark, View, ViewOff } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import './AuthPages.scss';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { updatePassword } = useAuth();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const isDark = theme === 'dark';

  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('🔍 Full URL:', window.location.href);
        console.log('🔍 URL search params:', window.location.search);
        console.log('🔍 URL hash:', window.location.hash);
        
        // Check URL parameters for password recovery tokens
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        const accessToken = urlParams.get('access_token') || hashParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token') || hashParams.get('refresh_token');
        const type = urlParams.get('type') || hashParams.get('type');
        
        console.log('🔍 Reset password URL params:', { 
          accessToken: !!accessToken, 
          refreshToken: !!refreshToken, 
          type,
        });
        
        // Check if we have recovery tokens
        if (type === 'recovery' && accessToken && refreshToken) {
          console.log('🔄 Setting session with recovery tokens...');
          
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (error) {
            console.error('❌ Session setup error:', error);
            setError(`Session setup failed: ${error.message}`);
            setIsValidSession(false);
          } else if (data.session?.user) {
            console.log('✅ Valid password recovery session established');
            setIsValidSession(true);
          } else {
            console.error('❌ No session after token setup');
            setError('Failed to establish session with reset tokens.');
            setIsValidSession(false);
          }
        } else {
          console.log('🔍 No recovery tokens found, checking existing session...');
          
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('❌ Session check error:', error);
            setError(`Session check failed: ${error.message}`);
            setIsValidSession(false);
          } else if (session?.user) {
            console.log('✅ Valid session found');
            setIsValidSession(true);
          } else {
            console.log('❌ No valid session found');
            setError('No valid session found. Please request a new password reset.');
            setIsValidSession(false);
          }
        }
      } catch (err) {
        console.error('❌ Session check error:', err);
        setError(`Unable to verify reset session: ${err.message}`);
        setIsValidSession(false);
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, []);

  const passwordRequirements = [
    { text: 'At least 8 characters', met: formData.password.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(formData.password) },
    { text: 'Contains a number', met: /[0-9]/.test(formData.password) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      const { error: updateError } = await updatePassword(formData.password);
      
      if (updateError) {
        throw updateError;
      }
      
      setSubmitted(true);
    setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewReset = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/forgot-password');
    } catch (err) {
      console.error('Error signing out:', err);
      navigate('/forgot-password');
    }
  };

  if (isCheckingSession) {
    return (
      <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`} data-testid="reset-password-page">
        <div className="auth-page__container auth-page__container--centered">
          <div className="auth-card">
            <div className="auth-card__header">
              <h2 className="auth-card__title">Verifying reset link...</h2>
              <p className="auth-card__subtitle">Please wait while we verify your password reset link.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidSession) {
    return (
      <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`} data-testid="reset-password-page">
        <div className="auth-page__container auth-page__container--centered">
          <div className="auth-card">
            <div className="auth-card__header">
              <h2 className="auth-card__title">Invalid Reset Link</h2>
              <p className="auth-card__subtitle">{error || 'This password reset link is invalid or has expired.'}</p>
            </div>
            {error && (
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={error}
                onClose={() => setError('')}
                className="auth-card__notification"
              />
            )}
            <div className="auth-card__actions">
              <Button kind="primary" onClick={handleRequestNewReset} className="auth-card__btn">
                Request New Password Reset
              </Button>
            </div>
            <p className="auth-card__footer">
              <span onClick={() => navigate('/login')} className="auth-card__link">Back to Login</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`} data-testid="reset-password-page">
      <div className="auth-page__container auth-page__container--centered">
        <div className="auth-card">
          <div className="auth-card__header auth-card__header--icon">
            <div className="auth-card__icon">
              <Password size={28} />
            </div>
            <h2 className="auth-card__title">Reset Password</h2>
            <p className="auth-card__subtitle">Create a new password for your account</p>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError('')}
              className="auth-card__notification"
            />
          )}

          {submitted ? (
            <div className="auth-card__success">
              <div className="auth-card__icon auth-card__icon--success">
                <Checkmark size={28} />
              </div>
              <h3 className="auth-card__success-title">Password Reset Successfully!</h3>
              <p className="auth-card__success-text">
                Your password has been changed. You can now sign in with your new password.
              </p>
              <Button kind="primary" onClick={() => navigate('/login')} className="auth-card__btn">
                Go to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-card__form">
              <div className="auth-card__fields">
                <div className="auth-card__password-field">
                  <TextInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    labelText="New Password"
                    placeholder="Enter new password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <button
                    type="button"
                    className="auth-card__password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <ViewOff size={20} /> : <View size={20} />}
                  </button>
                </div>

                <div className="auth-card__requirements">
                  {passwordRequirements.map((req, idx) => (
                    <div key={idx} className={`auth-card__requirement ${req.met ? 'auth-card__requirement--met' : ''}`}>
                      <Checkmark size={16} />
                      <span>{req.text}</span>
                    </div>
                  ))}
                </div>

                <TextInput
                  id="confirmPassword"
                  type="password"
                  labelText="Confirm Password"
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                />
              </div>

              <div className="auth-card__actions">
                <Button
                  kind="primary"
                  type="submit"
                  className="auth-card__btn"
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          )}

          <p className="auth-card__footer">
            <span onClick={() => navigate('/login')} className="auth-card__link">Back to Login</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
